import React, { useState, useEffect, useRef } from 'react';
import { Flex, Box, Tooltip } from '@chakra-ui/react';
import { KTag, KText } from '@/ui-library/components';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { MOBILE_BREAKPOINT } from '@/constants/mobile-breakpoints';

const getGroupColor = (
  groupSize: number
): { bgColor: string; color: string } => {
  const colorIndex = Math.abs(groupSize) % 6;

  const colorMap = [
    { bgColor: '#FAF0FA', color: '#960D96' },
    { bgColor: '#E5F4F4', color: '#0A5B5B' },
    { bgColor: 'pastel-3-light', color: 'pastel-3-dark' },
    { bgColor: 'pastel-4-light', color: 'pastel-4-dark' },
    { bgColor: 'pastel-5-light', color: 'pastel-5-dark' },
    { bgColor: 'pastel-6-light', color: 'pastel-6-dark' },
  ];

  return colorMap[colorIndex];
};

/**
 * Helper function to extract sequence number from requisition code
 * e.g., "VV-214-3" -> 3
 */
const getSequenceNumber = (code: string): number => {
  const parts = code.split('-');
  return parseInt(parts[parts.length - 1], 10) || 0;
};

// To display group requisition codes as tags
interface GroupRequisitionCodesProps {
  requisitions?: any[];
  codes?: string[];
  gap?: string;
  codeStyles?: any;
}

export const GroupRequisitionCodes: React.FC<GroupRequisitionCodesProps> = ({
  requisitions,
  codes,
  gap = '6px',
  codeStyles = {},
}) => {
  const groupHoverStyles = {
    bgColor: 'white',
    borderColor: 'border-color',
    color: 'secondary-700',
    borderWidth: '1px',
  };

  // Handle requisition objects
  if (requisitions && requisitions.length > 0) {
    const sortedRequisitions = [...requisitions].sort((a, b) => {
      return getSequenceNumber(a.code) - getSequenceNumber(b.code);
    });

    return (
      <Flex flexWrap="wrap" gap={gap} {...codeStyles}>
        {sortedRequisitions.map((req) => (
          <KTag
            key={req.id}
            value={req.code}
            color="secondary-700"
            _groupHover={groupHoverStyles}
          />
        ))}
      </Flex>
    );
  }

  // Handle code strings
  if (codes && codes.length > 0) {
    const sortedCodes = [...codes].sort((a, b) => {
      return getSequenceNumber(a) - getSequenceNumber(b);
    });

    return (
      <Flex flexWrap="wrap" gap={gap} {...codeStyles}>
        {sortedCodes.map((code) => (
          <KTag
            key={code}
            value={code}
            color="secondary-700"
            _groupHover={groupHoverStyles}
          />
        ))}
      </Flex>
    );
  }

  return null;
};

interface PartOfGroupTagProps {
  requisition: any;
  allRequisitions?: any[];
}

export const PartOfGroupTag: React.FC<PartOfGroupTagProps> = ({
  requisition,
  allRequisitions = [],
}) => {
  const isMobile = useIsMobile({ mobileBreakpoint: MOBILE_BREAKPOINT });
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMobile) {
      setIsTooltipOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsTooltipOpen(false);
      }
    };

    if (isTooltipOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTooltipOpen]);

  const {
    requisition_group_id,
    group_size,
    id: currentReqId,
    group,
  } = requisition as any;

  // Get the group ID from either field
  const groupId = requisition_group_id || group?.id;
  const isPartOfGroup = !!groupId;

  // Find all requisitions in the same group
  const allGroupRequisitions = React.useMemo(() => {
    if (!isPartOfGroup || allRequisitions.length === 0) {
      return [];
    }

    // Filter requisitions that belong to the same group
    const groupReqs = allRequisitions.filter((req) => {
      const reqGroupId = req.requisition_group_id || (req as any).group?.id;
      return reqGroupId && reqGroupId === groupId;
    });

    // Add current requisition if not already in the list
    const currentReqInList = groupReqs.some((req) => req.id === currentReqId);
    if (!currentReqInList) {
      groupReqs.push(requisition);
    }

    return groupReqs;
  }, [isPartOfGroup, allRequisitions, groupId, currentReqId, requisition]);

  // Get consistent color for this group based on group_size
  const groupColor = React.useMemo(() => {
    if (!isPartOfGroup || !group_size) return null;
    return getGroupColor(group_size);
  }, [isPartOfGroup, group_size]);

  const tooltipLabel =
    isPartOfGroup && allGroupRequisitions.length > 0 ? (
      <Flex flexDir="column" gap="8px" p="4px">
        <KText>Requisitions in this group</KText>
        <GroupRequisitionCodes requisitions={allGroupRequisitions} />
      </Flex>
    ) : undefined;

  if (!isPartOfGroup || !groupColor) {
    return null;
  }

  // Mobile view with click functionality
  if (isMobile) {
    return (
      <Box position="relative">
        <KTag
          value="Part of a group"
          cursor="pointer"
          color={groupColor.color}
          bgColor={groupColor.bgColor}
          width="max-content"
          size="xs-regular"
          whiteSpace="nowrap"
          onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          _groupHover={{
            bgColor: 'white',
            borderColor: 'border-color',
            color: 'secondary-700',
            borderWidth: '1px',
          }}
        />
        {isTooltipOpen && allGroupRequisitions.length > 0 && (
          <Box
            ref={tooltipRef}
            position="absolute"
            zIndex={9999}
            bg="white"
            color="secondary-700"
            p="12px"
            borderRadius="6px"
            minW="120px"
            maxW="280px"
            width="fit-content"
            border="1px solid"
            borderColor="border-color"
            boxShadow="drop-shadows-bottom"
            mt="4px"
            right="0"
            left="0"
          >
            <Flex flexDir="column" gap="8px" p="4px">
              <KText size="sm-medium">Requisitions in this group</KText>
              <GroupRequisitionCodes
                requisitions={allGroupRequisitions}
                gap="4px"
              />
            </Flex>
          </Box>
        )}
        {isTooltipOpen && allGroupRequisitions.length === 0 && (
          <Box
            ref={tooltipRef}
            position="absolute"
            zIndex={9999}
            bg="white"
            color="secondary-700"
            p="12px"
            borderRadius="6px"
            minW="120px"
            maxW="280px"
            width="fit-content"
            border="1px solid"
            borderColor="border-color"
            boxShadow="drop-shadows-bottom"
            mt="4px"
            right="0"
            left="0"
          >
            <KText size="sm-medium">No requisitions found in this group</KText>
          </Box>
        )}
      </Box>
    );
  }

  // Desktop view with tooltip functionality
  return (
    <Tooltip
      label={tooltipLabel}
      placement="bottom-start"
      bg="white"
      color="secondary-700"
      p="12px"
      borderRadius="6px"
      // maxW={isMobile ? 'calc(100vw - 32px)' : '400px'}
      maxW="400px"
      border="1px solid"
      borderColor="border-color"
      boxShadow="drop-shadows-bottom"
      isDisabled={isMobile}
    >
      <Box>
        <KTag
          value="Part of a group"
          cursor="pointer"
          color={groupColor.color}
          bgColor={groupColor.bgColor}
          width="max-content"
          size="xs-regular"
          _groupHover={{
            bgColor: 'white',
            borderColor: 'border-color',
            color: 'secondary-700',
            borderWidth: '1px',
          }}
        />
      </Box>
    </Tooltip>
  );
};
